import { HttpClient } from '@angular/common/http';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { DeviceDetectorService } from 'ngx-device-detector';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';

import { UserProfile, UserProfileResponse, UserType, UserPrivilege } from '../../shared/models/request/user-profile.response';
import { APP_CONFIG, APP_CONFIG_LOCAL } from './app-config.service';
import { SessionService } from './session.service';
import { Constants } from '../../shared/constants/constants';
import { COOKIE } from '../../modules/login/util/constants';

const EXPIRY_TIME = 60 * 30; // User Session Expiry Time period
const IDLE_TIME = 60 * 27; // User Idle Time period
const TIMEOUT_TIME = 60 * 3; // Idle Time-out warning period

@Injectable({
  providedIn: 'root'
})
export class UserService {

  userRole!: string;
  userRoleId!: string;
  userPrivilegesList: string[] = [];
  userPrivilegesIdList: number[] = [];
  adGroups: string[] = [];
  sessionId = '';
  jwtToken = '';
  firstName = '';
  lastName = '';
  email = '';
  ipAddress = Constants.DEFAULT_IP;
  accessLevels: string[] = [];
  authenticated = false;
  accountInfoAvailable = false;
  userName!: string;
  userProfile!: UserProfile | null;
  userIdentifier!: string;
  isAuntheticated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isTimeOut$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  ssoLoginErrorMessage$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  ticker!: number;
  timedOut = false;
  validity = 5 * 60;
  watcher!: any;
  currentIdleTime!: string;
  expiryTime!: Date;
  idleTime!: Date;
  allowIdleTracking = true;
  sessionModal = false;
  selfFunded = '';
  fullyInsured = '';
  manualRated = '';
  renderer!: Renderer2;

  mouseMoveListener!: () => void;
  mouseClickListener!: () => void;
  keyUpListener!: () => void;

  constructor(
    private httpClient: HttpClient,
    private sessionService: SessionService,
    private cookieService: CookieService,
    private router: Router,
    private modalService: NgbModal,
    private deviceService: DeviceDetectorService,
    private rendererFactory: RendererFactory2
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  userProfileRequest(requestBody: any): Observable<UserProfileResponse> {
    if (window.location.hostname === 'localhost') {
      return this.httpClient.get<UserProfileResponse>(APP_CONFIG_LOCAL.LOCAL_USER_PROFILE);
    } else {
      return this.httpClient.post<UserProfileResponse>(`${APP_CONFIG.API_BASE_URL}${APP_CONFIG.USER_PROFILE}`, requestBody);
    }
  }

  /**
  * Getter for UserProfile.
  * @returns {UserProfile} The current user's profile.
  */
  get getUserProfile(): UserProfile {
    return this.userProfile as UserProfile;
  }

  /**
   * Setter for UserProfile.
   * @param {UserProfile} userProfile - The user's profile to be set.
   */
  set setUserProfile(userProfile: UserProfile) {
    this.userProfile = userProfile;
    // Update other setters related to user profile
    if (userProfile) {
      this.setUserIdentifier = userProfile.userIdentifier;
      this.setUserRole = userProfile.userType;
      this.setUserPrivilegesList = userProfile.userPrivileges;
      this.setUserRoleId = userProfile.userType;
      this.setUserPrivilegesIdList = userProfile.userPrivileges;
      // this.setAccessLevels = userProfile.adAccessLevel;
      this.setAccessLevels = userProfile.adAccessLevel ?
        userProfile.adAccessLevel : atob(this.cookieService.get(COOKIE.DP_ADM_AL)) || '';
      this.isAccountInfoAvailable = !userProfile.isAccountInfoMissing;
    }
  }

  /**
   * Checks if the user is authenticated.
   * @returns {boolean} True if the user is authenticated, false otherwise.
   */
  get isAuthenticated() {
    return this.authenticated; // update the condition during integration
  }

  /**
   * Sets the authentication status of the user.
   * @param {boolean} authenticated - The authentication status to be set.
   */
  set setAuthenticated(authenticated: boolean) {
    this.authenticated = authenticated;
  }

  /**
   * Checks if the user account info is available.
   * @returns {boolean} True if the user account info is available, false otherwise.
   */
  get isAccountInfoAvailable() {
    return this.accountInfoAvailable;
  }

  /**
   * Sets the availability status of the user account info.
   * @param {boolean} isAccountInfoAvailable - The availability status to be set.
   */
  set isAccountInfoAvailable(isAccountInfoAvailable: boolean) {
    this.accountInfoAvailable = isAccountInfoAvailable;
  }

  /**
   * Getter for UserName.
   * @returns {string} The current user's name.
   */
  get getUserName(): string {
    return this.userName || '';
  }

  /**
   * Setter for UserName.
   * @param {string} userName - The user's name to be set.
   */
  set setUserName(userName: string) {
    this.userName = userName;
  }

  /**
   * Getter for FirstName.
   * @returns {string} The current user's first name.
   */
  get getFirstName(): string {
    return this.firstName || '';
  }

  /**
   * Setter for FirstName.
   * @param {string} firstName - The user's first name to be set.
   */
  set setFirstName(firstName: string) {
    this.firstName = firstName;
  }

  /**
   * Getter for LastName.
   * @returns {string} The current user's last name.
   */
  get getLastName(): string {
    return this.lastName || '';
  }

  /**
   * Setter for LastName.
   * @param {string} lastName - The user's last name to be set.
   */
  set setLastName(lastName: string) {
    this.lastName = lastName;
  }

  /**
   * Getter for Email.
   * @returns {string} The current user's email address.
   */
  get getEmail(): string {
    return this.email || '';
  }

  /**
   * Setter for Email.
   * @param {string} email - The user's email address to be set.
   */
  set setEmail(email: string) {
    this.email = email;
  }

  /**
   * Getter for UserIdentifier.
   * @returns {string} The current user's identifier.
   */
  get getUserIdentifier(): string {
    return this.userIdentifier || '';
  }

  /**
   * Setter for UserIdentifier.
   * @param {string} userIdentifier - The user's identifier to be set.
   */
  set setUserIdentifier(userIdentifier: string) {
    this.userIdentifier = userIdentifier;
  }

  /**
   * Getter for UserRole.
   * @returns {string} The current user's role.
   */
  get getUserRole(): string {
    return this.userRole;
  }

  /**
   * Setter for UserRole.
   * @param {UserType} userType - The user's role to be set.
   */
  set setUserRole(userType: UserType) {
    this.userRole = userType?.adminUserRole || '';
  }

  /**
   * Getter for UserRoleId.
   * @returns {string} The current user's role id.
   */
  get getUserRoleId(): string {
    return this.userRoleId;
  }

  /**
   * Setter for UserRole.
   * @param {UserType} userType - The user's role to be set.
   */
  set setUserRoleId(userType: UserType) {
    this.userRoleId = userType?.adminUserTypeId || '';
  }

  /**
   * Getter for Session ID
   * @returns {string} sessionId
   */
  get getSessionID(): string {
    if (!this.sessionId) {
      this.sessionId = uuidv4();
      this.cookieService.set(COOKIE.DP_ADM_SID, btoa(this.sessionId),
        { domain: window.location.hostname, path: '/', sameSite: 'Strict', secure: true });
    }
    return `${this.getUserIdentifier ? this.getUserIdentifier : Constants.UNAUTHUSER}::${this.sessionId}`;
  }

  /**
   * Setter for Session ID
   * @param {string} sessionId
   */
  set setSessionID(sessionId: string) {
    this.sessionId = sessionId;
  }

  /**
   * Getter for IP Address
   * @returns {string} ipAddress
   */
  get getIpAddress(): string {
    return this.ipAddress || Constants.DEFAULT_IP;
  }

  /**
   * Setter for ipAddress.
   * @param {string} ipAddress
   */
  set setIpAddress(ipAddress: string) {
    this.ipAddress = ipAddress;
  }

  /**
   * Getter for JWT Token
   * @returns {string} jwtToken
   */
  get getJwtToken(): string {
    return this.jwtToken || '';
  }

  /**
   * Setter for JWT Token
   * @param {string} jwtToken
   */
  set setJwtToken(jwtToken: string) {
    this.jwtToken = jwtToken;
  }

  set setSelfFunded(selfFund: string) {
    this.selfFunded = selfFund;
  }

  get getSelfFunded(): string {
    return this.selfFunded;
  }

  set setFullyInsured(FI: string) {
    this.fullyInsured = FI;
  }

  get getFullyInsured(): string {
    return this.fullyInsured;
  }

  set setManual(manual: string) {
    this.manualRated = manual;
  }

  get getManual(): string {
    return this.manualRated;
  }

  /**
   * Getter for UserPrivilegesList.
   * @returns {string[]} The current user's privileges list.
   */
  get getUserPrivilegesList(): string[] {
    return this.userPrivilegesList;
  }

  /**
   * Setter for UserPrivilegesList.
   * @param {UserPrivilege[]} userPrivilegesList - The user's privileges list to be set.
   */
  set setUserPrivilegesList(userPrivilegesList: UserPrivilege[]) {
    this.userPrivilegesList = userPrivilegesList?.map((userPrivilege: UserPrivilege) => userPrivilege.portalFeatureText) || [];
  }

  /**
   * Getter for UserPrivilegesIdList.
   * @returns {string[]} The current user's privileges ID list.
   */
  get getUserPrivilegesIdList(): number[] {
    return this.userPrivilegesIdList;
  }

  /**
   * Setter for UserPrivilegesIdList.
   * @param {UserPrivilege[]} userPrivilegesList - The user's privileges ID list to be set.
   */
  set setUserPrivilegesIdList(userPrivilegesList: UserPrivilege[]) {
    this.userPrivilegesIdList = userPrivilegesList?.map((userPrivilege: UserPrivilege) => +userPrivilege.portalFeatureId) || [];
  }

  /**
   * Getter for AccessLevels.
   * @returns {string[]} The current user's access levels.
   */
  get getAccessLevels(): string[] {
    return this.accessLevels;
  }

  /**
   * Setter for AccessLevels.
   * @param {string} accessLevels - The user's access levels to be set.
   */
  set setAccessLevels(accessLevels: string) {
    this.accessLevels = accessLevels ? accessLevels?.split(':') : [];
  }

  /**
   * Getter for AdGroups.
   * @returns {string[]} The current user's Azure AD groups.
   */
  get getAdGroups(): string[] {
    return this.adGroups;
  }

  /**
   * Setter for AdGroups.
   * @param {string[]} adGroups - The user's Azure AD groups to be set.
   */
  set setAdGroups(adGroups: string[]) {
    this.adGroups = adGroups || [];
  }

  /**
 * Method to log out the user
 */
  public logoutUser() {
    this.sessionService.clear();
    localStorage.removeItem('AD_USER_PROFILE');
    this.cookieService.deleteAll('/', window.location.hostname, true, 'Strict');
    this.setAuthenticated = false;
    this.isAuntheticated$.next(false);
    this.userRole = '';
    this.userPrivilegesList = [];
    this.userRoleId = '';
    this.userPrivilegesIdList = [];
    this.userName = '';
    this.userIdentifier = '';
    this.jwtToken = '';
    this.accessLevels = [];
    this.adGroups = [];
    this.firstName = '';
    this.lastName = '';
    this.email = '';
    this.userProfile = null;
    this.removeListeners();
  }

  /**
 * Method to handle session timeout modal
 */
  handleSessionTimeout() {
    this.mouseMoveListener = this.renderer.listen('document', 'mousemove', () => this.onMouseMove());
    this.mouseClickListener = this.renderer.listen('document', 'click', () => this.onMouseClick());
    this.keyUpListener = this.renderer.listen('document', 'keyup', () => this.onKeyUp());

    this.currentIdleTime = moment().format('hh:mma');
    this.expiryTime = new Date(new Date().getTime() + (EXPIRY_TIME * 1000));
    this.idleTime = new Date(new Date().getTime() + (IDLE_TIME * 1000));
    this.ticker = 0;
    this.sessionModal = false;
    this.allowIdleTracking = true;

    const forceLogout = setInterval(() => {
      if (!this.isAuthenticated) {
        clearInterval(forceLogout);
        return;
      }
      this.ticker += 1;
      const cd = new Date();
      if (this.ticker === (IDLE_TIME + TIMEOUT_TIME) ||
        cd >= this.expiryTime) {
        setTimeout(() => {
          clearInterval(forceLogout);
          this.modalService.dismissAll();
          const wasAzureLogin = localStorage.getItem('ENABLE_AZURE_LOGIN') === 'true';
          this.logoutUser();
          this.router.navigate([wasAzureLogin ? '/landing' : 'login']);
        }, 1000);
      } else if (this.ticker === IDLE_TIME || cd >= this.idleTime && cd < this.expiryTime) {
        if (!this.sessionModal) {
          this.allowIdleTracking = false;
          this.showWarning();
        }
      }
    }, 1000);
  }

  // Update on mouse move movement
  onMouseMove = () => {
    if (this.allowIdleTracking) {
      this.resetIdleTrackers();
    }
  }

  // Update on mouse click
  onMouseClick = () => {
    if (this.allowIdleTracking) {
      this.resetIdleTrackers();
    }
  }

  // Update on key up movement
  onKeyUp = () => {
    if (this.allowIdleTracking) {
      this.resetIdleTrackers();
    }
  }

  removeListeners() {
    if (this.mouseMoveListener) {
      this.mouseMoveListener();
    }
    if (this.mouseClickListener) {
      this.mouseClickListener();
    }
    if (this.keyUpListener) {
      this.keyUpListener();
    }
  }

  /**
   *  Method to show time out modal
   */
  showWarning() {
    this.modalService.dismissAll();
    this.watcher = setInterval(() => {
      this.validity = Math.round((this.expiryTime.getTime() - new Date().getTime()) / 1000);
      if (this.validity === 0) {
        this.timedOut = true;
        clearInterval(this.watcher);
      }
    }, 1000);
    this.sessionModal = true;
    this.isTimeOut$.next(true);
  }

  /**
   * Method to reset the timer
   */
  resetIdleTrackers() {
    this.ticker = 0;
    this.expiryTime = new Date(new Date().getTime() + (EXPIRY_TIME * 1000));
    this.idleTime = new Date(new Date().getTime() + (IDLE_TIME * 1000));
    this.sessionModal = false;
    this.currentIdleTime = moment().format('hh:mma');
  }

  /**
   * Method to get current Idle time
   */
  get getCurrentIdleTime() {
    return this.currentIdleTime;
  }

  /**
   * Method to extend session timeout
   */
  extendSession() {
    clearInterval(this.watcher);
    this.modalService.dismissAll();
    this.resetIdleTrackers();
    this.allowIdleTracking = true;
  }

  /**
   * Method to get browser info
   */
  getCurrentBrowser() {
    const deviceInfo = this.deviceService.getDeviceInfo();
    return deviceInfo.browser;
  }
}
